import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { EASE, scrollToId } from "./Reveal";

const LINKS = [
  { id: "what-i-do", label: "What we do", testid: "nav-link-what-i-do" },
  { id: "work", label: "Work", testid: "nav-link-work" },
  { id: "pricing", label: "Pricing", testid: "nav-link-pricing" },
  { id: "faq", label: "FAQ", testid: "nav-link-faq" },
  { id: "contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "bg-[#050505]/70 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-10 lg:px-16">
        <button
          data-testid="nav-logo"
          onClick={() => (open ? go("top") : scrollToId("top"))}
          className="font-display text-xl tracking-wide text-white transition-colors duration-300 hover:text-[#E5C158] sm:text-2xl"
        >
          CFDigital&nbsp;Co<span className="text-[#E5C158]">.</span>
        </button>

        <div className="hidden items-center gap-10 sm:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollToId(l.id)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#D4AF37] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            data-testid="nav-call-button"
            href="tel:+61492884214"
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-[border-color,color,box-shadow] duration-300 hover:border-[#D4AF37]/60 hover:text-[#E5C158] sm:px-5"
          >
            <Phone className="h-3.5 w-3.5" />
            Call
          </a>
          <a
            data-testid="nav-cta-inquire"
            href="mailto:CFDigiCo@gmail.com"
            className="hidden items-center rounded-full border border-[#D4AF37]/40 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5C158] transition-[background-color,border-color,color,box-shadow] duration-300 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] sm:inline-flex"
          >
            Inquire
          </a>
          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-[border-color,color] duration-300 hover:border-[#D4AF37]/60 hover:text-[#E5C158] sm:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 top-20 z-[-1] flex h-[calc(100dvh-5rem)] flex-col justify-between bg-[#050505]/95 px-6 pb-10 pt-10 backdrop-blur-xl sm:hidden"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: EASE, delay: 0.06 * i }}
                  onClick={() => go(l.id)}
                  className="flex items-baseline gap-4 py-4 text-left border-b border-white/[0.06]"
                >
                  <span className="font-mono text-[10px] tracking-[0.3em] text-[#D4AF37]/70">
                    0{i + 1}
                  </span>
                  <span className="font-display text-3xl font-light tracking-tight text-white">
                    {l.label}
                  </span>
                </motion.button>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE, delay: 0.35 }}
              className="flex flex-col gap-4"
            >
              <a
                data-testid="nav-mobile-inquire"
                href="mailto:CFDigiCo@gmail.com"
                className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black"
              >
                Inquire — CFDigiCo@gmail.com
              </a>
              <a
                data-testid="nav-mobile-call"
                href="tel:+61492884214"
                className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white"
              >
                <Phone className="h-3.5 w-3.5" />
                Call 0492 884 214
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}